import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

// Component ported from https://codepen.io/JuanFuentes/full/rgXKGQ

interface TextPressureProps {
  text?: string
  tag?: string
  fontFamily?: string
  width?: boolean
  weight?: boolean
  italic?: boolean
  alpha?: boolean
  flex?: boolean
  stroke?: boolean
  scale?: boolean
  textColor?: string
  strokeColor?: string
  className?: string
  minFontSize?: number
}

// The font itself is loaded globally via the Google Fonts link in config/head.ts.
// Passing another fontFamily only works if that family is requested there too.

export default /*#__PURE__*/_defineComponent({
  __name: 'text-pressure',
  props: {
    text: { default: 'AI-ALL-IN' },
    tag: { default: 'h1' },
    fontFamily: { default: 'Roboto Flex' },
    width: { type: Boolean, default: true },
    weight: { type: Boolean, default: true },
    italic: { type: Boolean, default: false },
    alpha: { type: Boolean, default: false },
    flex: { type: Boolean, default: true },
    stroke: { type: Boolean, default: false },
    scale: { type: Boolean, default: false },
    textColor: { default: '#FFFFFF' },
    strokeColor: { default: '#FF0000' },
    className: { default: '' },
    minFontSize: { default: 24 }
  },
  setup(__props: any) {

const props = __props as { text: string, tag: string, fontFamily: string, width: boolean, weight: boolean, italic: boolean, alpha: boolean, flex: boolean, stroke: boolean, scale: boolean, textColor: string, strokeColor: string, className: string, minFontSize: number };



const containerRef = ref<HTMLDivElement | null>(null)
const titleRef = ref<HTMLHeadingElement | null>(null)
const spansRef = ref<(HTMLSpanElement | null)[]>([])

const mouseRef = ref({ x: 0, y: 0 })
const cursorRef = ref({ x: 0, y: 0 })

const fontSize = ref(props.minFontSize)
const scaleY = ref(1)
const lineHeight = ref(1)

const chars = computed(() => props.text.split(''))

const setSpanRef = (el: any, index: number) => {
  spansRef.value[index] = el as HTMLSpanElement | null
}

const dist = (a: { x: number; y: number }, b: { x: number; y: number }) => {
  const dx = b.x - a.x
  const dy = b.y - a.y
  return Math.sqrt(dx * dx + dy * dy)
}

const handleMouseMove = (e: MouseEvent) => {
  cursorRef.value.x = e.clientX
  cursorRef.value.y = e.clientY
}

const handleTouchMove = (e: TouchEvent) => {
  const t = e.touches[0]
  cursorRef.value.x = t.clientX
  cursorRef.value.y = t.clientY
}

const setSize = () => {
  if (!containerRef.value || !titleRef.value) {
    return
  }

  const { width: containerW, height: containerH } = containerRef.value.getBoundingClientRect()

  let newFontSize = containerW / (chars.value.length / 2)
  newFontSize = Math.max(newFontSize, props.minFontSize)

  fontSize.value = newFontSize
  scaleY.value = 1
  lineHeight.value = 1

  nextTick(() => {
    if (!titleRef.value) {
      return
    }
    const textRect = titleRef.value.getBoundingClientRect()

    if (props.scale && textRect.height > 0) {
      const yRatio = containerH / textRect.height
      scaleY.value = yRatio
      lineHeight.value = yRatio
    }
  })
}

let rafId: number

const animate = () => {
  mouseRef.value.x += (cursorRef.value.x - mouseRef.value.x) / 15
  mouseRef.value.y += (cursorRef.value.y - mouseRef.value.y) / 15

  if (titleRef.value) {
    const titleRect = titleRef.value.getBoundingClientRect()
    const maxDist = titleRect.width / 2

    spansRef.value.forEach((span) => {
      if (!span) {
        return
      }

      const rect = span.getBoundingClientRect()
      const charCenter = {
        x: rect.x + rect.width / 2,
        y: rect.y + rect.height / 2
      }

      const d = dist(mouseRef.value, charCenter)

      const getAttr = (distance: number, minVal: number, maxVal: number) => {
        const val = maxVal - Math.abs((maxVal * distance) / maxDist)
        return Math.max(minVal, val + minVal)
      }

      // getAttr spans [minVal, minVal + maxVal], so the second argument is the
      // axis span, not its upper bound. Roboto Flex: wdth 25-151, wght 100-1000.
      const wdth = props.width ? Math.floor(getAttr(d, 25, 126)) : 100
      const wght = props.weight ? Math.floor(getAttr(d, 100, 900)) : 400
      const italVal = props.italic ? getAttr(d, 0, 1).toFixed(2) : 0
      const alphaVal = props.alpha ? getAttr(d, 0, 1).toFixed(2) : 1

      span.style.opacity = alphaVal.toString()
      span.style.fontVariationSettings = `'wght' ${wght}, 'wdth' ${wdth}, 'ital' ${italVal}`
    })
  }

  rafId = requestAnimationFrame(animate)
}

const dynamicClassName = computed(() => {
  return [props.className, props.flex ? 'flex' : '', props.stroke ? 'stroke' : '']
    .filter(Boolean)
    .join(' ')
})

const dynamicStyles = computed(() => {
  return `
    .flex {
      display: flex;
      justify-content: space-between;
    }

    .stroke span {
      position: relative;
      color: ${props.textColor};
    }
    .stroke span::after {
      content: attr(data-char);
      position: absolute;
      left: 0;
      top: 0;
      color: transparent;
      z-index: -1;
      -webkit-text-stroke-width: 3px;
      -webkit-text-stroke-color: ${props.strokeColor};
    }

    .text-pressure-title {
      color: ${props.textColor};
    }
  `
})

onMounted(() => {
  // Initialize mouse position
  if (containerRef.value) {
    const { left, top, width, height } = containerRef.value.getBoundingClientRect()
    mouseRef.value.x = left + width / 2
    mouseRef.value.y = top + height / 2
    cursorRef.value.x = mouseRef.value.x
    cursorRef.value.y = mouseRef.value.y
  }

  // Add event listeners
  window.addEventListener('mousemove', handleMouseMove)
  window.addEventListener('touchmove', handleTouchMove, { passive: false })
  window.addEventListener('resize', setSize)

  // Initialize size and start animation
  setSize()
  animate()
})

onUnmounted(() => {
  // Remove event listeners
  window.removeEventListener('mousemove', handleMouseMove)
  window.removeEventListener('touchmove', handleTouchMove)
  window.removeEventListener('resize', setSize)

  // Cancel animation frame
  if (rafId) {
    cancelAnimationFrame(rafId)
  }
})

// Watch for prop changes that should trigger resize
watch([() => props.scale, () => props.text], () => {
  setSize()
})

return { __sfc: true,props, containerRef, titleRef, spansRef, mouseRef, cursorRef, fontSize, scaleY, lineHeight, chars, setSpanRef, dist, handleMouseMove, handleTouchMove, setSize, rafId, animate, dynamicClassName, dynamicStyles }
}

})